<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from 'vue'
import { useI18n } from 'vue-i18n'
import AppButton from '../AppButton/AppButton.vue'
import AppNotice from '../AppNotice/AppNotice.vue'
import type { NoticeTone } from '../AppNotice/notice-types'
import * as store from './toast-store'
import type { ToastRecord, ToastType } from './toast-types'

const props = defineProps<{
  toast: ToastRecord
  /** 0 = front (newest); higher = further back in the stack. */
  index: number
  /** Toasts in this toast's position group (front stays visible; rest hide). */
  total: number
  /** Container hovered/focused, or the toaster's `expand` prop - lifts the stack. */
  expanded: boolean
  /** Sum of the measured heights of the toasts in front of this one, in px. */
  heightBefore: number
  /** Vertical anchor edge - drives peek/lift direction and swipe-away axis. */
  yPosition: 'top' | 'bottom'
  /** Horizontal anchor - drives which way a horizontal swipe dismisses. */
  xPosition: 'left' | 'center' | 'right'
  visibleToasts: number
  closeButton: boolean
  globalDuration: number
}>()

const emit = defineEmits<{
  height: [id: number | string, height: number]
}>()

const DEFAULT_DURATION = 4000
const SWIPE_THRESHOLD = 45
const SWIPE_VELOCITY = 0.11
const RELEASE_MS = 200

const TONE: Record<ToastType, NoticeTone> = {
  default: 'neutral',
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  loading: 'progress',
}

const tone = computed(() => TONE[props.toast.type])
const noticeTitle = computed(() => (props.toast.description ? props.toast.title : undefined))
const noticeMessage = computed(() => props.toast.description ?? props.toast.title)

const { t } = useI18n()

const dismissible = computed(() => props.toast.dismissible !== false)
const isUrgent = computed(() => props.toast.type === 'error' || props.toast.type === 'warning')
const showClose = computed(
  () => dismissible.value && (props.toast.closeButton ?? props.closeButton),
)
const hidden = computed(() => !props.expanded && props.index >= props.visibleToasts)

const CustomRender = () => props.toast.render?.(props.toast) ?? null

const swipe = ref({ x: 0, y: 0 })
const dragging = ref(false)
const releasing = ref(false)

const transform = computed(() => {
  const i = props.index
  const ySign = props.yPosition === 'top' ? 1 : -1
  let stack: string
  if (props.expanded) {
    stack = `translateY(calc(var(--spacing-6) * ${i * ySign} + ${props.heightBefore * ySign}px)) scale(1)`
  } else {
    const scale = `calc(1 - ${i} * var(--toast-scale-step))`
    stack = `translateY(calc(var(--spacing-6) * ${i * ySign})) scale(${scale})`
  }
  const { x, y } = swipe.value
  return x !== 0 || y !== 0 ? `translate(${x}px, ${y}px) ${stack}` : stack
})

const cardStyle = computed<CSSProperties>(() => ({
  transform: transform.value,
  zIndex: String(props.total - props.index),
  opacity: hidden.value ? '0' : '1',
  pointerEvents: hidden.value ? 'none' : 'auto',
}))

const transitionClass = computed(() => {
  if (dragging.value) return 'transition-none'
  if (releasing.value) return 'transition-[transform,opacity] duration-base ease-soft-exit'
  return 'transition-[transform,opacity] duration-moderate ease-soft'
})

const el = ref<HTMLLIElement | null>(null)
const documentHidden = ref(false)

const duration = computed(() => props.toast.duration ?? props.globalDuration ?? DEFAULT_DURATION)
const paused = computed(() => props.expanded || documentHidden.value)

let timer: ReturnType<typeof setTimeout> | undefined
let remaining = 0
let startedAt = 0

function clearTimer() {
  if (timer !== undefined) {
    clearTimeout(timer)
    timer = undefined
  }
}

function onElapse() {
  clearTimer()
  props.toast.onAutoClose?.(props.toast)
  store.dismiss(props.toast.id)
}

function run() {
  clearTimer()
  if (paused.value || !Number.isFinite(remaining)) return
  startedAt = Date.now()
  timer = setTimeout(onElapse, remaining)
}

function startTimer() {
  clearTimer()
  remaining = duration.value
  run()
}

function pauseTimer() {
  if (timer === undefined) return
  clearTimer()
  remaining -= Date.now() - startedAt
}

watch(paused, (isPaused) => (isPaused ? pauseTimer() : run()))
watch(duration, () => startTimer())

function onVisibility() {
  documentHidden.value = document.hidden
}

let resizeObserver: ResizeObserver | undefined

function reportHeight() {
  if (el.value) emit('height', props.toast.id, el.value.offsetHeight)
}

let startX = 0
let startY = 0
let startT = 0
let releaseTimer: ReturnType<typeof setTimeout> | undefined

function onPointerDown(event: PointerEvent) {
  if (!dismissible.value) return
  dragging.value = true
  releasing.value = false
  startX = event.clientX
  startY = event.clientY
  startT = event.timeStamp
  el.value?.setPointerCapture(event.pointerId)
}

function allowsX(delta: number) {
  if (props.xPosition === 'center') return true
  return props.xPosition === 'right' ? delta > 0 : delta < 0
}
function allowsY(delta: number) {
  return props.yPosition === 'top' ? delta < 0 : delta > 0
}
function damp(delta: number, allowed: boolean) {
  return allowed ? delta : delta / (1.5 + Math.abs(delta) / 20)
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value) return
  const dx = event.clientX - startX
  const dy = event.clientY - startY
  swipe.value = { x: damp(dx, allowsX(dx)), y: damp(dy, allowsY(dy)) }
}

function onPointerUp(event: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  if (el.value?.hasPointerCapture(event.pointerId)) {
    el.value.releasePointerCapture(event.pointerId)
  }
  const { x, y } = swipe.value
  const ax = allowsX(x) ? x : 0
  const ay = allowsY(y) ? y : 0
  const travel = Math.abs(ax) > Math.abs(ay) ? ax : ay
  const velocity = Math.abs(travel) / Math.max(1, event.timeStamp - startT)

  if (Math.abs(travel) >= SWIPE_THRESHOLD || velocity > SWIPE_VELOCITY) {
    onClose()
    return
  }
  releasing.value = true
  swipe.value = { x: 0, y: 0 }
  if (releaseTimer !== undefined) clearTimeout(releaseTimer)
  releaseTimer = setTimeout(() => {
    releasing.value = false
    releaseTimer = undefined
  }, RELEASE_MS)
}

function onAction(event: MouseEvent) {
  props.toast.action?.onClick(event)
  store.dismiss(props.toast.id)
}
function onCancel(event: MouseEvent) {
  props.toast.cancel?.onClick(event)
  store.dismiss(props.toast.id)
}
function onClose() {
  store.dismiss(props.toast.id)
}

onMounted(() => {
  if (typeof document === 'undefined') return
  reportHeight()
  if (typeof ResizeObserver !== 'undefined' && el.value) {
    resizeObserver = new ResizeObserver(reportHeight)
    resizeObserver.observe(el.value)
  }
  document.addEventListener('visibilitychange', onVisibility)
  documentHidden.value = document.hidden
  startTimer()
})

onBeforeUnmount(() => {
  clearTimer()
  if (releaseTimer !== undefined) clearTimeout(releaseTimer)
  resizeObserver?.disconnect()
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibility)
  }
})
</script>

<template>
  <li
    ref="el"
    :class="[
      'absolute inset-x-0 list-none',
      yPosition === 'top' ? 'top-0 origin-top' : 'bottom-0 origin-bottom',
      transitionClass,
    ]"
    :style="cardStyle"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
  >
    <div
      :role="isUrgent ? 'alert' : 'status'"
      :aria-live="isUrgent ? 'assertive' : 'polite'"
      aria-atomic="true"
    >
      <component :is="CustomRender" v-if="toast.render" />

      <AppNotice
        v-else
        shape="toast"
        :tone="tone"
        :title="noticeTitle"
        :message="noticeMessage"
        :icon="toast.icon"
        :dismissible="showClose"
        :dismiss-label="t('primitives.toast.close')"
        class="touch-none select-none"
        @dismiss="onClose"
      >
        <template v-if="toast.action || toast.cancel" #actions>
          <AppButton
            v-if="toast.action"
            size="xs"
            variant="secondary"
            :label="toast.action.label"
            @click="onAction"
          />
          <AppButton
            v-if="toast.cancel"
            size="xs"
            variant="ghost"
            :label="toast.cancel.label"
            @click="onCancel"
          />
        </template>
      </AppNotice>
    </div>
  </li>
</template>
